import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConnectMt5Wizard from './ConnectMt5Wizard';
import { useConfirm } from './ConfirmDialog';
import {
  listLiveAccounts,
  listLiveLinks,
  revokeLiveLink,
  formatSigned,
  CONNECTION_LABELS,
  CONSISTENCY_LABELS,
} from './liveTrackDataFeeds';

// Live MT5 tab — the user's real trading accounts, as reported by their own terminal.
//
// Kept separate from the Signals tab on purpose. A cloud signal is executed by us and can be
// called verified; this is a real account reporting its own numbers. Blurring the two would
// undermine the one claim that makes the marketplace trustworthy, so the distinction is drawn
// in the layout, not just in a footnote.

function ConnectionPill({ state }) {
  const label = CONNECTION_LABELS[state] || state;
  if (state === 'live') return <span className="signal-pill ok">{label}</span>;
  if (state === 'idle') return <span className="signal-pill warn">{label}</span>;
  return <span className="signal-pill">{label}</span>;
}

function ConsistencyPill({ consistency }) {
  if (!consistency?.assessed) return null;
  const label = CONSISTENCY_LABELS[consistency.verdict] || consistency.verdict;
  const tone = consistency.verdict === 'consistent' ? 'ok'
    : consistency.verdict === 'partial' ? 'warn' : 'danger';
  return (
    <span className={`signal-pill ${tone}`} title={`${consistency.matched} of ${consistency.signalTrades} cloud trades matched`}>
      {label} · {Math.round(consistency.score)}%
    </span>
  );
}

function AccountCard({ entry, onOpen }) {
  const { account, stats, consistency } = entry;
  return (
    <article className="signal-card">
      <header>
        <div>
          <h4>{account.broker || 'MetaTrader account'}</h4>
          <p className="muted-text">
            #{account.accountLogin} · {account.accountType} · {account.currency}
          </p>
        </div>
        <ConnectionPill state={account.connection} />
      </header>

      <div className="signal-card-metrics">
        <div>
          <span className="muted-text">Return</span>
          <strong className={stats.returnPct >= 0 ? 'up' : 'down'}>
            {stats.returnPct >= 0 ? '+' : ''}{stats.returnPct.toFixed(2)}%
          </strong>
        </div>
        <div>
          <span className="muted-text">Trades</span>
          <strong>{stats.trades}</strong>
        </div>
        <div>
          <span className="muted-text">Win rate</span>
          <strong>{stats.trades ? `${stats.winRate.toFixed(0)}%` : '—'}</strong>
        </div>
        <div>
          <span className="muted-text">Net</span>
          <strong className={stats.netProfit >= 0 ? 'up' : 'down'}>{formatSigned(stats.netProfit)}</strong>
        </div>
      </div>

      {consistency?.assessed && (
        <p className="live-consistency-note">
          <ConsistencyPill consistency={consistency} />
        </p>
      )}

      <footer>
        <span className="muted-text">
          Equity {Number(account.equity || 0).toFixed(2)} {account.currency}
        </span>
        <button type="button" className="primary-cta compact-button" onClick={() => onOpen(account.id)}>
          Open
        </button>
      </footer>
    </article>
  );
}

function LiveTrackingTab({ savedStrategies }) {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [error, setError] = useState('');
  const [confirm, confirmDialog] = useConfirm();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [accountResult, linkResult] = await Promise.all([listLiveAccounts(), listLiveLinks()]);
      setAccounts(accountResult.accounts || []);
      setLinks(linkResult.links || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="section-card">
      <h3>Live MetaTrader accounts</h3>
      <p className="muted-text">
        Track a real or demo MT5 account from any device. Your EA reports its own results, so
        these figures are <strong>self-reported</strong> — where you also run the same strategy
        as a cloud signal, we compare the two and show how closely they agree.
      </p>

      {error && <p className="signal-error">{error}</p>}

      <div className="inline-actions">
        <button type="button" className="primary-cta compact-button" onClick={() => setWizardOpen(true)}>
          Connect MetaTrader
        </button>
        <button type="button" className="ghost-button compact-button" onClick={refresh}>Refresh</button>
      </div>

      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : accounts.length === 0 ? (
        <div className="signal-empty">
          <h4>No accounts connected yet</h4>
          <p className="muted-text">
            Connect MetaTrader to see your EA's real results here — balance, trades and drawdown,
            updated automatically while your terminal is running. Takes about two minutes.
          </p>
          <p className="muted-text">
            Running the <strong>signal copier</strong> instead? That account appears here on its
            own, with nothing to connect — including when it is copying an imported MQL5 signal
            rather than a strategy you built.
          </p>
        </div>
      ) : (
        <div className="signal-card-grid">
          {accounts.map((entry) => (
            <AccountCard key={entry.account.id} entry={entry} onOpen={(id) => navigate(`/live/${id}`)} />
          ))}
        </div>
      )}

      {links.length > 0 && (
        <div className="live-links">
          <h4>Connection tokens</h4>
          <p className="muted-text">
            Each token identifies one MetaTrader setup. Revoking a token stops it reporting
            immediately; the history it already collected is kept.
          </p>
          <div className="table-shell">
            <table>
              <thead>
                <tr><th>Label</th><th>Status</th><th>Last report</th><th>Accounts</th><th /></tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td>
                      {link.label}
                      {link.source === 'copier' && (
                        <>{' '}<span className="signal-pill">Copier</span></>
                      )}
                    </td>
                    <td><ConnectionPill state={link.connection} /></td>
                    <td className="muted-text">
                      {link.lastSeenAt ? new Date(link.lastSeenAt).toLocaleString() : 'Never'}
                    </td>
                    <td>{(link.accounts || []).length}</td>
                    <td>
                      <button
                        type="button"
                        className="ghost-button compact-button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: 'Revoke this token?',
                            body: 'Your EA will stop reporting immediately. The results already '
                              + 'collected under this token are kept, and you can connect again '
                              + 'with a new token at any time.',
                            confirmLabel: 'Revoke token',
                            tone: 'danger',
                          });
                          if (!ok) return;
                          await revokeLiveLink(link.id).catch((err) => setError(err.message));
                          refresh();
                        }}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {confirmDialog}

      {wizardOpen && (
        <ConnectMt5Wizard
          strategies={savedStrategies}
          onClose={() => { setWizardOpen(false); refresh(); }}
          onConnected={(account) => { setWizardOpen(false); refresh(); navigate(`/live/${account.id}`); }}
        />
      )}
    </div>
  );
}

export default LiveTrackingTab;
